import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { chartTheme } from '../utils/chartTheme';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { useTheme } from '../context/ThemeContext';
import HelpHint from './HelpHint';

// Simple cache for IndicatorTile queries to improve speed on mobile/slow connections
const tileCache = new Map<string, any[]>();
const MAX_TILE_CACHE_SIZE = 100;

function addToTileCache(key: string, data: any[]) {
  if (tileCache.size >= MAX_TILE_CACHE_SIZE) {
    const firstKey = tileCache.keys().next().value;
    if (firstKey) tileCache.delete(firstKey);
  }
  tileCache.set(key, data);
}

interface IndicatorTileProps {
  title: string;
  kommunId: string | null;
  skolaId: string | null;
  kpiId: string;
  unit: string;
  helpText?: string;
  helpTitle?: string;
  compareScope?: 'municipality' | 'national';
}

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, unit = '', theme }: any) => {
  if (active && payload && payload.length) {
    const formatValue = (value: number) => {
      if (value === null || value === undefined) return 'N/A';
      return Number(value).toFixed(1);
    };
    
    return (
      <div
        className="rounded-lg border shadow-lg p-3"
        style={{ background: theme.tooltipBg, borderColor: theme.grid, color: theme.tooltipFg }}
      >
        <p className="text-sm font-medium" style={{ color: theme.tooltipFg }}>{`År: ${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${formatValue(entry.value)}${unit}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const IndicatorTile: React.FC<IndicatorTileProps> = ({ 
  title, 
  kommunId, 
  skolaId, 
  kpiId, 
  unit,
  helpText,
  helpTitle,
  compareScope = 'municipality'
}) => {
  const { query } = useDuckDB();
  const { theme: themeMode } = useTheme();
  const [totalData, setTotalData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = chartTheme(themeMode);
  const colors = useMemo(
    () => ({
      school: theme.series.school || '#059669',
      municipality: theme.series.reference || '#1f2937',
    }),
    [theme.series.school, theme.series.reference]
  );
  const themeKey = useMemo(
    () => [themeMode, theme.axis, colors.school, colors.municipality, theme.grid].join('|'),
    [themeMode, theme.axis, colors.school, colors.municipality, theme.grid]
  );

  // Fetch chart data
  const fetchChartData = useCallback(async () => {
    const cacheKey = `${kommunId}-${skolaId}-${kpiId}-${compareScope}`;
    const cachedData = tileCache.get(cacheKey);
    if (cachedData) {
      setTotalData(cachedData);
      return;
    }

    const sql = `
      WITH school_data AS (
        SELECT year, value AS school_value
        FROM gold_kpi_trend
        WHERE entity_id = :skolaId AND kpi_id = :kpiId AND gender = 'T'
      ),
      ref_avg AS (
        ${compareScope === 'municipality'
          ? `SELECT year, AVG(value) AS ref_value
             FROM gold_kpi_trend
             WHERE kpi_id = :kpiId AND gender = 'T'
               AND municipality_id = :kommunId AND value IS NOT NULL
             GROUP BY year`
          : `SELECT year, AVG(value) AS ref_value
             FROM gold_kpi_trend
             WHERE kpi_id = :kpiId AND gender = 'T' AND value IS NOT NULL
             GROUP BY year`}
      )
      SELECT y.year, sd.school_value, ra.ref_value as muni_value
      FROM (SELECT year FROM school_data UNION SELECT year FROM ref_avg) y
      LEFT JOIN school_data sd ON sd.year = y.year
      LEFT JOIN ref_avg ra ON ra.year = y.year
      ORDER BY y.year;
    `;
    
    try {
      const data = await query(sql, { skolaId, kpiId, kommunId });
      addToTileCache(cacheKey, data);
      setTotalData(data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setTotalData([]);
    }
  }, [query, skolaId, kommunId, kpiId, compareScope]);

  // Load data
  useEffect(() => {
    if (kommunId && skolaId) {
      setLoading(true);
      setTotalData([]);
      
      fetchChartData().finally(() => {
        setLoading(false);
      });
    }
  }, [kommunId, skolaId, kpiId, fetchChartData]);

  if (loading) {
    return (
      <section className="rounded-lg bg-card ring-1 ring-border p-3 md:p-4">
        <h3 className="text-sm font-semibold mb-2">{title}</h3>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Laddar...</div>
        </div>
      </section>
    );
  }

  if (totalData.length === 0) {
    return (
      <section className="rounded-lg bg-card ring-1 ring-border p-3 md:p-4">
        <h3 className="text-sm font-semibold mb-2">{title}</h3>
        <div className="h-48 flex items-center justify-center text-muted-foreground">
          <p className="text-sm">Ingen data tillgänglig</p>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg bg-card ring-1 ring-border p-3 md:p-4">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold flex-1 text-foreground/90">{title}</h3>
        {helpText && (
          <HelpHint 
            kind="indicator"
            shortText={helpText}
            longText={helpText}
            title={helpTitle || 'Vad visar denna indikator?'}
          />
        )}
      </div>
      <div className="h-48 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart key={themeKey} data={totalData} margin={{ top: 8, right: 12, left: 12, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
            <XAxis 
              dataKey="year" 
              type="category"
              allowDuplicatedCategory={false}
              tick={{ fontSize: 11, fill: theme.axis }}
              tickLine={{ stroke: theme.axis }}
              axisLine={{ stroke: theme.axis }}
            />
            <YAxis 
              domain={['dataMin - 5%', 'dataMax + 5%']}
              tick={{ fontSize: 11, fill: theme.axis }}
              tickLine={{ stroke: theme.axis }}
              axisLine={{ stroke: theme.axis }}
              tickCount={4}
              tickFormatter={(value) => Number(value).toFixed(1)}
            />
            <Tooltip
              content={<CustomTooltip unit={unit} theme={theme} />}
              contentStyle={{ background: theme.tooltipBg, color: theme.tooltipFg, border: `1px solid ${theme.grid}` }}
            />
            <Line 
              type="monotone" 
              dataKey="school_value" 
              stroke={colors.school} 
              strokeWidth={2}
              dot={{ fill: colors.school, strokeWidth: 2, r: 3 }}
              name="Enhet"
            />
            <Line 
              type="monotone" 
              dataKey="muni_value" 
              stroke={colors.municipality} 
              strokeWidth={2}
              dot={{ fill: colors.municipality, strokeWidth: 2, r: 3 }}
              name={compareScope === 'municipality' ? 'Kommun' : 'Riket'}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

export default IndicatorTile;
